import {
  ArrowLeftRight,
  Calendar,
  CalendarClock,
  CreditCard,
  Landmark,
  LayoutList,
  LogOut,
  type LucideIcon,
  Settings,
  UserCog,
  Users,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18n.js';
import type { Words } from '../i18n/words.js';
import { useSchool } from '../school/SchoolGate.js';
import { Lockup, Preferences } from '../ui/parts.js';

type Section = keyof Words['shell']['sections'];

const SECTIONS: { key: Section; path: string; icon: LucideIcon }[] = [
  { key: 'today', path: '/', icon: CalendarClock },
  { key: 'schedule', path: '/schedule', icon: Calendar },
  { key: 'catalog', path: '/catalog', icon: LayoutList },
  { key: 'plans', path: '/plans', icon: CreditCard },
  { key: 'members', path: '/members', icon: Users },
  { key: 'payments', path: '/payments', icon: Landmark },
  { key: 'staff', path: '/staff', icon: UserCog },
  { key: 'settings', path: '/settings', icon: Settings },
];

/**
 * The console inside one school: Rythmo's shell, the school named and badged at the top of the
 * sidebar, its colour on the primary action and the active section (PRD §6). M1-05 ships the
 * frame; each section arrives with its own feature.
 */
export function Shell() {
  const { colours } = useSchool();
  const { t } = useI18n();
  // Keyed by route, so each section's heading makes its entrance.
  const { pathname } = useLocation();

  const brand = (
    colours === null
      ? {}
      : {
          '--action-primary': colours.action,
          '--action-primary-hover': colours.actionHover,
          '--text-on-brand': colours.onAction,
          '--school-ink-light': colours.inkLight,
          '--school-ink-dark': colours.inkDark,
        }
  ) as CSSProperties;

  return (
    <div className={colours === null ? 'shell' : 'shell shell-branded'} style={brand}>
      <Sidebar />
      <main className="shell-main" key={pathname}>
        <Routes>
          <Route index element={<Today />} />
          {SECTIONS.slice(1).map((section) => (
            <Route
              key={section.key}
              path={section.path.slice(1)}
              element={<Later title={t.shell.sections[section.key]} />}
            />
          ))}
          <Route path="*" element={<Today />} />
        </Routes>
      </main>
    </div>
  );
}

function Sidebar() {
  const { school, logoUrl, canSwitch, switchSchool } = useSchool();
  const { signOut } = useAuth();
  const { t, m, locale } = useI18n();

  return (
    <aside className="side">
      <div className="side-top">
        <Lockup />
      </div>

      <div className="side-school">
        {logoUrl !== null ? (
          <img className="side-school-badge" src={logoUrl} alt="" />
        ) : (
          <span className="side-school-badge side-school-initial" aria-hidden>
            {school.name.trim().charAt(0).toLocaleUpperCase()}
          </span>
        )}
        <span className="side-school-text">
          <span className="side-school-name">{school.name}</span>
          <span className="side-school-role">
            {capitalised(m.roles[topRole(school.roles)], locale)}
          </span>
        </span>
      </div>

      <nav className="side-nav" aria-label={t.shell.nav}>
        <ul>
          {SECTIONS.map(({ key, path, icon: Icon }) => (
            <li key={key}>
              <NavLink to={path} end={path === '/'} className="side-link">
                <Icon size={18} strokeWidth={1.5} aria-hidden />
                <span>{t.shell.sections[key]}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="side-foot">
        {canSwitch && (
          <button type="button" className="side-action" onClick={switchSchool}>
            <ArrowLeftRight size={18} strokeWidth={1.5} aria-hidden />
            <span>{t.shell.switchSchool}</span>
          </button>
        )}
        <button type="button" className="side-action" onClick={() => void signOut()}>
          <LogOut size={18} strokeWidth={1.5} aria-hidden />
          <span>{t.signOut}</span>
        </button>
        <div className="side-preferences">
          <Preferences />
        </div>
      </div>
    </aside>
  );
}

/** A shared word where it starts a label: the catalogue's words are lowercase (ADR-0064). */
function capitalised(word: string, locale: string): string {
  return word.charAt(0).toLocaleUpperCase(locale) + word.slice(1);
}

/** The role a console screen names first: an owner who also manages is an owner. */
function topRole(roles: readonly ('OWNER' | 'MANAGER' | 'TEACHER' | 'MEMBER')[]) {
  return roles.includes('OWNER') ? 'OWNER' : roles.includes('MANAGER') ? 'MANAGER' : 'MEMBER';
}

function Today() {
  const { user } = useAuth();
  const { school, me } = useSchool();
  const { t, m, locale } = useI18n();
  const name = user?.profile.name ?? user?.profile.email ?? me.email;

  return (
    <section className="page" aria-labelledby="page-title">
      <h1 id="page-title" className="page-title">
        {t.shell.hello(name)}
      </h1>
      <p className="page-lead">
        {t.shell.at(school.name, capitalised(m.roles[topRole(school.roles)], locale))} ·{' '}
        <time dateTime={new Date().toISOString().slice(0, 10)}>{today(m.intlLocale)}</time>
      </p>
      <section className="page-part" aria-labelledby="today-sessions">
        <h2 id="today-sessions" className="page-part-title">
          {t.shell.todaySessions}
        </h2>
        <p className="page-part-text">{t.shell.todaySessionsBody}</p>
      </section>
    </section>
  );
}

/** "joi, 4 noiembrie": the desk works by the day. */
function today(intlLocale: string): string {
  return new Intl.DateTimeFormat(intlLocale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());
}

function Later({ title }: { title: string }) {
  const { t } = useI18n();
  return (
    <section className="page" aria-labelledby="page-title">
      <h1 id="page-title" className="page-title">
        {title}
      </h1>
      <p className="page-lead">{t.shell.comingSoon}</p>
    </section>
  );
}
